import { createAsyncThunk } from '@reduxjs/toolkit';
import { subscriptionsApi } from '../api/subscriptions.api';
import type {
  MySubscription,
  SubscriptionPlan,
  SubscriptionUsageRecord,
  CanUseSubscriptionResponse,
  SubscriptionsError,
  FreezeInfo,
  FreezeResponse,
  UnfreezeResponse,
  FreezeHistoryRecord,
} from '../api/subscriptions.types';

export const fetchMySubscription = createAsyncThunk<
  MySubscription | null,
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/fetchMy', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getMySubscription();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to fetch subscription',
    });
  }
});

export const fetchSubscriptionPlans = createAsyncThunk<
  SubscriptionPlan[],
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getPlans();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to fetch plans',
    });
  }
});

export const fetchSubscriptionHistory = createAsyncThunk<
  SubscriptionUsageRecord[],
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/fetchHistory', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getHistory();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to fetch history',
    });
  }
});

export const checkCanUseSubscription = createAsyncThunk<
  CanUseSubscriptionResponse,
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/checkCanUse', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.canUse();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to check subscription',
    });
  }
});

export const fetchFreezeInfo = createAsyncThunk<
  FreezeInfo | null,
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/fetchFreezeInfo', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getFreezeInfo();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to fetch freeze info',
    });
  }
});

export const freezeSubscription = createAsyncThunk<
  FreezeResponse,
  { reason?: string } | void,
  { rejectValue: SubscriptionsError }
>('subscriptions/freeze', async (payload, { rejectWithValue }) => {
  try {
    const reason = payload && 'reason' in payload ? payload.reason : undefined;
    return await subscriptionsApi.freeze(reason);
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.description || error.response?.data?.message || 'Failed to freeze subscription',
    });
  }
});

export const unfreezeSubscription = createAsyncThunk<
  UnfreezeResponse,
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/unfreeze', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.unfreeze();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.description || error.response?.data?.message || 'Failed to unfreeze subscription',
    });
  }
});

export const fetchFreezeHistory = createAsyncThunk<
  FreezeHistoryRecord[],
  void,
  { rejectValue: SubscriptionsError }
>('subscriptions/fetchFreezeHistory', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getFreezeHistory();
  } catch (error: any) {
    return rejectWithValue({
      code: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to fetch freeze history',
    });
  }
});
